/* sb-dc-cartlog.js — tell "add to cart failed" apart from "customer walked away".

   As of 2026-08-29 the funnel goes dark after the proof step: dc_events has
   design_start and template_apply, storage has the print file, and then
   nothing until a Shopify order appears. When no order appears we cannot say
   whether /cart/add.js refused the line (unresolved variant, sold out,
   quantity outside the price table) or whether the customer simply left the
   cart page. This writes that one missing step.

   Three events, all into the existing dc_events table:
     cart_add_try   — the proof was approved and the add started
     cart_add_ok    — Shopify accepted the line
     cart_add_fail  — it did not, with the reason

   ref = the design id (same id as the dc-prints folder), so an event can be
   lined up with the print file and, for cart_add_ok, with the order that
   carries that URL in UPLOADED FILE.

   No Supabase key is embedded here. The page already talks to the same
   project (templates load, design_start), so the credentials are lifted off
   the first REST call that goes past and reused. If none has gone past yet
   the event waits in a queue and is flushed when one does.

   Read-only with respect to the order: every failure path swallows its own
   error. Nothing in here can block or alter an add to cart. */
(function () {
  'use strict';

  var REST = null;          // { url, headers } captured from the app's own call
  var queue = [];           // events waiting for credentials

  /* ---------- credential capture ---------------------------------- */

  function headerBag(init, input) {
    var out = {};
    var h = (init && init.headers) || (input && input.headers) || null;
    if (!h) return out;
    try {
      if (typeof h.forEach === 'function' && !Array.isArray(h)) {
        h.forEach(function (v, k) { out[String(k).toLowerCase()] = v; });
      } else if (Array.isArray(h)) {
        h.forEach(function (p) { out[String(p[0]).toLowerCase()] = p[1]; });
      } else {
        Object.keys(h).forEach(function (k) { out[k.toLowerCase()] = h[k]; });
      }
    } catch (e) {}
    return out;
  }

  function noteRest(url, init, input) {
    if (REST) return;
    if (!/\.supabase\.co\/rest\/v1\//.test(url)) return;
    var h = headerBag(init, input);
    if (!h.apikey) return;
    REST = {
      url: url.split('/rest/v1/')[0] + '/rest/v1/dc_events',
      headers: {
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal',
        apikey: h.apikey,
        Authorization: h.authorization || ('Bearer ' + h.apikey)
      }
    };
    flush();
  }

  /* ---------- writing ---------------------------------------------- */

  function post(rows) {
    try {
      origFetch.call(window, REST.url, {
        method: 'POST',
        headers: REST.headers,
        body: JSON.stringify(rows),
        keepalive: true          // survives the jump to /cart
      }).catch(function () {});
    } catch (e) {}
  }

  function flush() {
    if (!REST || !queue.length) return;
    var rows = queue.splice(0, queue.length);
    post(rows);
  }

  function log(kind, ref, meta) {
    var row = { kind: kind, ref: ref ? String(ref).slice(0, 200) : null, meta: meta || null };
    if (REST) post([row]); else if (queue.length < 40) queue.push(row);
  }

  /* ---------- reading the attempt ---------------------------------- */

  function designId() {
    try {
      var d = window.__dcDesignId;
      if (d) return d;
      var m = String(window.__dcLastPrintUrl || '').match(/dc-prints\/(dsn_[a-z0-9]+)/i);
      if (m) return m[1];
    } catch (e) {}
    return null;
  }

  function snapshot() {
    var out = {};
    try {
      var p = window.__dcSelectedProduct || {};
      out.handle = p.handle || null;
      out.unit = p.unit || null;
      var s = window.__dcSpec || null;
      if (s) {
        out.w = s.widthIn || null;
        out.h = s.heightIn || null;
        out.qty = s.quantity || null;
        out.finishing = (s.finishing && (s.finishing.id || s.finishing)) || null;
      }
      if (typeof window.__dcOverrideOn === 'function') out.override = !!window.__dcOverrideOn();
      var a = (typeof window.__dcAddons === 'function') ? window.__dcAddons() : null;
      if (a && a.length) out.addons = a.length;
    } catch (e) {}
    return out;
  }

  /* Pull what we can out of the body the cart module is posting: the variant
     it resolved and the print file it attached. Read only — the body itself
     is passed through untouched. */
  function readCartBody(body) {
    var out = {};
    try {
      var d = JSON.parse(body);
      var items = Array.isArray(d.items) ? d.items : [d];
      out.lines = items.length;
      var first = items[0] || {};
      out.variantId = first.id || null;
      out.qty = first.quantity || null;
      var props = first.properties || {};
      var url = props['Print File'] || props['PRINT FILE'] || props['UPLOADED FILE'] || '';
      var m = String(url).match(/dc-prints\/(dsn_[a-z0-9]+)/i);
      if (m) out.designId = m[1];
      out.isDesignCenter = /dc-prints\//.test(String(url));
    } catch (e) {}
    return out;
  }

  /* ---------- fetch wrapper ---------------------------------------- */

  var origFetch = window.fetch;
  if (typeof origFetch !== 'function' || origFetch.__dcLogWrapped) return;

  var wrapped = function (input, init) {
    var url = '';
    try { url = (typeof input === 'string') ? input : (input && input.url) || ''; } catch (e) {}

    try { noteRest(url, init, input); } catch (e) {}

    var isAdd = false;
    try { isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url); } catch (e) {}
    if (!isAdd) return origFetch.apply(this, arguments);

    var info = {};
    try {
      var body = init && init.body;
      if (typeof body === 'string') info = readCartBody(body);
    } catch (e) {}

    /* Only design-center lines are logged. A normal product-page add that
       happens to run on the same session is none of our business. */
    if (!info.isDesignCenter) return origFetch.apply(this, arguments);

    var meta = snapshot();
    meta.variantId = info.variantId || null;
    meta.lines = info.lines || null;
    if (info.qty) meta.qty = info.qty;
    var ref = info.designId || designId();

    var t0 = Date.now();
    return origFetch.apply(this, arguments).then(function (res) {
      var m = Object.assign({}, meta, { status: res.status, ms: Date.now() - t0 });
      if (res.ok) {
        log('cart_add_ok', ref, m);
      } else {
        /* Clone so the cart module still gets to read its own response. */
        res.clone().json().then(function (j) {
          m.reason = (j && (j.description || j.message)) || null;
          log('cart_add_fail', ref, m);
        }).catch(function () {
          log('cart_add_fail', ref, m);
        });
      }
      return res;
    }, function (err) {
      var m = Object.assign({}, meta, { status: 0, ms: Date.now() - t0, reason: String((err && err.message) || err).slice(0, 200) });
      log('cart_add_fail', ref, m);
      throw err;
    });
  };
  wrapped.__dcLogWrapped = true;
  window.fetch = wrapped;

  /* ---------- __dcAddToCart wrapper --------------------------------
     Catches the failures that never reach fetch at all: variant unresolved,
     sold out, quantity outside the price table, upload failed. Those return
     false (or throw) before any request is made, and are exactly the cases
     that look identical to "customer left" in the data we have today. */

  function wrapAdd(orig) {
    var f = function () {
      var ref = designId();
      var meta = snapshot();
      log('cart_add_try', ref, meta);

      var out;
      try {
        out = orig.apply(this, arguments);
      } catch (e) {
        log('cart_add_fail', ref, Object.assign({}, meta, { stage: 'sync', reason: String((e && e.message) || e).slice(0, 200) }));
        throw e;
      }
      if (!out || typeof out.then !== 'function') {
        if (out === false) log('cart_add_fail', ref, Object.assign({}, meta, { stage: 'module', reason: 'returned false' }));
        return out;
      }
      return out.then(function (ok) {
        /* A non-true result means the module gave up before posting. */
        if (ok !== true) {
          log('cart_add_fail', ref, Object.assign({}, meta, { stage: 'module', reason: 'resolved ' + String(ok) }));
        }
        return ok;
      }, function (e) {
        log('cart_add_fail', ref, Object.assign({}, meta, { stage: 'async', reason: String((e && e.message) || e).slice(0, 200) }));
        throw e;
      });
    };
    f.__dcLogWrapped = true;
    return f;
  }

  var done = false, tries = 0;
  var timer = setInterval(function () {
    if (!done && typeof window.__dcAddToCart === 'function' && !window.__dcAddToCart.__dcLogWrapped) {
      window.__dcAddToCart = wrapAdd(window.__dcAddToCart);
      done = true;
    }
    if (done || ++tries > 100) clearInterval(timer);
  }, 200);

  /* Anything still queued when the page goes away (the jump to /cart) gets
     one last chance. */
  window.addEventListener('pagehide', function () { flush(); });
})();
